import { create } from 'zustand';
import type { ReactNode } from 'react';

/** 오버레이 render 함수가 받는 컨텍스트. */
export interface OverlayContext {
  open: boolean;
  close: () => void;
}

type OverlayRender = (ctx: OverlayContext) => ReactNode;

/** present 옵션. `key` 지정 시 같은 key 오버레이가 이미 열려 있으면 재present 하지 않는다(멱등). */
export interface PresentOptions {
  key?: string;
}

interface OverlayEntry {
  id: string;
  key?: string;
  open: boolean;
  render: OverlayRender;
}

interface OverlayState {
  entries: OverlayEntry[];
  present: (render: OverlayRender, options?: PresentOptions) => string;
  close: (id: string) => void;
  closeAll: () => void;
  remove: (id: string) => void;
}

/** Radix Dialog/Drawer 기본 exit 애니메이션 길이 — close 후 unmount 까지 지연(끊김 방지). */
const CLOSE_DELAY_MS = 200;

let counter = 0;

/**
 * 범용 명령형 오버레이 store. 컴포넌트 밖에서도 `.getState()` 로
 * 접근해야 해서 Context 가 아닌 external store 를 쓴다. terms 등 도메인은 전혀 모른다.
 */
export const useOverlayStore = create<OverlayState>()((set, get) => ({
  entries: [],
  present(render, options) {
    const key = options?.key;
    if (key !== undefined) {
      // 멱등 — 같은 key 가 이미 열려 있으면 새로 쌓지 않고 기존 id 반환(연타·중복 트리거 방지).
      const existing = get().entries.find((e) => e.key === key && e.open);
      if (existing) return existing.id;
    }
    const id = `overlay-${++counter}`;
    set((s) => ({ entries: [...s.entries, { id, key, open: true, render }] }));
    return id;
  },
  close(id) {
    set((s) => ({
      entries: s.entries.map((e) => (e.id === id ? { ...e, open: false } : e)),
    }));
    // open:false 로 먼저 플립 → exit 애니메이션 후 배열에서 제거.
    setTimeout(() => get().remove(id), CLOSE_DELAY_MS);
  },
  closeAll() {
    // teardown 용(라우트 이동·세션 만료) — 애니메이션 없이 **즉시** 비운다. 엔트리가 사라지면
    // OverlayHost 가 재렌더되며 모달이 unmount → 내부 query observer 가 파괴되어, 만료 redirect 시
    // `queryClient.clear()` 가 mount 된 observer 를 재구독→refetch→401 로 무한 루프 도는 것을 차단.
    set({ entries: [] });
  },
  remove(id) {
    set((s) => ({ entries: s.entries.filter((e) => e.id !== id) }));
  },
}));

/**
 * ReactNode 를 앱 루트 슬롯에 mount 하는 명령형 API — 컴포넌트 밖(순수 함수)에서도 호출 가능.
 * `options.key` 로 멱등 present(같은 key 가 열려 있으면 재mount 안 함).
 */
export function present(render: OverlayRender, options?: PresentOptions): string {
  return useOverlayStore.getState().present(render, options);
}

/**
 * 열린 오버레이를 전부 즉시 닫는 명령형 API — 라우트 이동·세션 teardown 등 호출처가
 * 페이지 스코프를 끝낼 때. 컴포넌트 밖(composition root 콜백 등)에서도 호출 가능.
 */
export function closeAllOverlays(): void {
  useOverlayStore.getState().closeAll();
}
